// src/stores/dashboardStore.ts
// ============================================
// CEO Dashboard 状态管理（Sprint 8 Epic 4）
//
// 职责：
//   1. currentTab - 当前激活的 Dashboard 页面（CEO/Marketing/CRM/Projects/Hermes）
//   2. autoRefresh - 是否开启自动刷新（5s 轮询）
//   3. timeRange - 时间范围筛选（今日/本周/本月）
//   4. cache - 数据缓存（避免切换 Tab 时白屏）
//
// 设计：zustand 极简 store，不掺业务逻辑
// ============================================

import { create } from "zustand";

// ============================================
// 类型定义
// ============================================

/** Dashboard 五个 Tab 标识 */
export type DashboardTab =
  | "ceo" // 第一页：经营总览
  | "marketing" // 第二页：营销分析
  | "crm" // 第三页：客户流水线
  | "projects" // 第四页：项目矩阵
  | "hermes"; // 第五页：AI 运营

/** 时间范围（影响 KPI 与趋势图统计口径） */
export type DashboardTimeRange = "today" | "week" | "month";

/** 缓存条目：保存最近一次成功的数据 + 时间戳 */
export interface DashboardCacheEntry<T = unknown> {
  data: T | null;
  /** 是否处于降级模式（数据不可用） */
  degraded: boolean;
  /** 最后更新时间（ISO） */
  lastUpdated: string | null;
}

/** 整体缓存：每个 Tab 一份 */
export type DashboardCache = Record<
  DashboardTab,
  DashboardCacheEntry
>;

// ============================================
// Store 接口
// ============================================

interface DashboardState {
  /** 当前激活的 Tab */
  currentTab: DashboardTab;
  /** 自动刷新开关（默认 true） */
  autoRefresh: boolean;
  /** 时间范围（默认今日） */
  timeRange: DashboardTimeRange;
  /** 数据缓存（避免切 Tab 时白屏） */
  cache: DashboardCache;

  // ---- Actions ----
  setTab: (tab: DashboardTab) => void;
  setAutoRefresh: (on: boolean) => void;
  setTimeRange: (range: DashboardTimeRange) => void;
  /** 写入某 Tab 的缓存 */
  setCache: <T>(
    tab: DashboardTab,
    entry: { data: T | null; degraded: boolean; lastUpdated: string | null }
  ) => void;
  /** 清空所有缓存（用户手动强制刷新时调用） */
  clearCache: () => void;
}

// ============================================
// 默认缓存
// ============================================

const EMPTY_ENTRY: DashboardCacheEntry = {
  data: null,
  degraded: true,
  lastUpdated: null,
};

const DEFAULT_CACHE: DashboardCache = {
  ceo: { ...EMPTY_ENTRY },
  marketing: { ...EMPTY_ENTRY },
  crm: { ...EMPTY_ENTRY },
  projects: { ...EMPTY_ENTRY },
  hermes: { ...EMPTY_ENTRY },
};

// ============================================
// Store 实现
// ============================================

export const useDashboardStore = create<DashboardState>((set) => ({
  currentTab: "ceo",
  autoRefresh: true,
  timeRange: "today",
  cache: DEFAULT_CACHE,

  setTab: (tab) => set({ currentTab: tab }),
  setAutoRefresh: (on) => set({ autoRefresh: on }),
  setTimeRange: (range) => set({ timeRange: range }),
  setCache: (tab, entry) =>
    set((state) => ({
      cache: {
        ...state.cache,
        [tab]: {
          data: entry.data,
          degraded: entry.degraded,
          lastUpdated: entry.lastUpdated,
        },
      },
    })),
  clearCache: () => set({ cache: { ...DEFAULT_CACHE } }),
}));

// ============================================
// Tab 元信息（供导航栏渲染）
// ============================================

export interface DashboardTabMeta {
  id: DashboardTab;
  label: string;
  /** 中文副标题 */
  subtitle: string;
  /** 强调色（HEX） */
  color: string;
}

export const DASHBOARD_TABS: DashboardTabMeta[] = [
  {
    id: "ceo",
    label: "CEO",
    subtitle: "经营总览",
    color: "#10B981", // 翠绿
  },
  {
    id: "marketing",
    label: "Marketing",
    subtitle: "营销分析",
    color: "#F59E0B", // 琥珀
  },
  {
    id: "crm",
    label: "CRM",
    subtitle: "客户流水线",
    color: "#3B82F6", // 蓝
  },
  {
    id: "projects",
    label: "Projects",
    subtitle: "项目矩阵",
    color: "#A855F7", // 紫
  },
  {
    id: "hermes",
    label: "Hermes",
    subtitle: "AI 运营",
    color: "#EC4899", // 粉
  },
];

export default useDashboardStore;
